import { Button, PageBreadcrumb, Spinner, TabsV2, Tooltip } from "@/modules/common/components";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import { formatCnpj } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { generatePath, useParams, useSearchParams } from "react-router-dom";
import useManagerCompanyDetails from "./hooks/useManagerCompanyDetails";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { ArrowLeft, PencilLineIcon, ShareIcon } from "lucide-react";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { MANAGER_DETAILS_TAB_PARAM, MANAGER_DETAILS_TABS } from "../../constants/ManagerDetailsTabs";
import GeneralDataTab from "./components/GeneralDataTab";
import ManagerUsersTab from "./components/ManagerUsersTab";

const ManagerDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canUserSee } = useSessionStore();
  const navigateBack = useNavigateBack(ROUTES.MANAGER.LIST);

  const { data: company, isLoading, isError, error } = useManagerCompanyDetails({ id: id!, enabled: !!id });

  useEffect(() => {
    if (isError) {
      toast.error(error?.message || "Erro ao carregar gestora.");
      navigateBack();
    }
  }, [isError, error]);

  const activeTab =
    searchParams.get(MANAGER_DETAILS_TAB_PARAM) === MANAGER_DETAILS_TABS.USERS
      ? MANAGER_DETAILS_TABS.USERS
      : MANAGER_DETAILS_TABS.GENERAL_DATA;

  const onTabChange = (tab: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === MANAGER_DETAILS_TABS.GENERAL_DATA) next.delete(MANAGER_DETAILS_TAB_PARAM);
        else next.set(MANAGER_DETAILS_TAB_PARAM, tab);
        return next;
      },
      { replace: true },
    );
  };

  const TAB_DATA = useMemo(
    () =>
      company
        ? [
            { key: MANAGER_DETAILS_TABS.GENERAL_DATA, label: "Dados Gerais", content: <GeneralDataTab company={company} /> },
            { key: MANAGER_DETAILS_TABS.USERS, label: "Usuários", content: <ManagerUsersTab managementCompanyId={company.id} /> },
          ]
        : [],
    [company],
  );

  const subtitle = company ? [company.corporateName, company.cnpj ? formatCnpj(company.cnpj) : null].filter(Boolean).join(" · ") : "";

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Gestoras", to: ROUTES.MANAGER.LIST }, ...(company ? [{ label: company.name }] : [])]} />
      {isLoading && company === undefined ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : !!company ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <Tooltip label={company.name}>
                <p className="text-[22px] font-bold text-ui-primary truncate md:line-clamp-2 md:whitespace-normal">{company.name}</p>
              </Tooltip>
              <p className="h-5 text-[14px] text-ui-text-muted">{subtitle}</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outlined" label="Voltar" icon={<ArrowLeft />} className="w-full sm:w-auto" onClick={navigateBack} />
              <Button label="Abrir ambiente" disabled={true} icon={<ShareIcon />} className="w-full sm:w-auto" />
              {canUserSee(AdminUserRole.ADMIN) && (
                <Button
                  label="Editar Gestora"
                  route={generatePath(ROUTES.MANAGER.EDIT, { id: company.id })}
                  icon={<PencilLineIcon />}
                  className="w-full sm:w-auto"
                />
              )}
            </div>
          </div>

          <TabsV2 value={activeTab} onTabChange={onTabChange} tabData={TAB_DATA} />
        </>
      ) : null}
    </div>
  );
};

export default ManagerDetails;
